import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";

// Site::Shared::LandArea::Component の Stimulus コントローラ。
// landIeul: TabbedLandArea のタブ切替 + GetNearbyPrefectureIndices による公開判定を再現。

interface Prefecture {
  id: number;
  name: string;
  url: string;
}

const TABS = [
  { label: "土地活用", pathSuffix: "", kind: null as string | null },
  { label: "アパート経営", pathSuffix: "apartment/", kind: "condominium" },
  { label: "マンション経営", pathSuffix: "mansionkeiei/", kind: "condominium" },
  { label: "戸建賃貸", pathSuffix: "kodate/", kind: "house" },
  { label: "店舗・賃貸併用", pathSuffix: "mixed-use-housing/", kind: "condominium" },
];

const REGIONS = [
  { name: "北海道・東北", start: 0, end: 7 },
  { name: "関東", start: 7, end: 14 },
  { name: "北陸・甲信越", start: 14, end: 20 },
  { name: "東海", start: 20, end: 24 },
  { name: "近畿", start: 24, end: 30 },
  { name: "中国", start: 30, end: 35 },
  { name: "四国", start: 35, end: 39 },
  { name: "九州・沖縄", start: 39, end: 47 },
];

const QUERY = `query GetNearbyPrefectureIndices($landUtilizationMethods: [LandUtilizationMethod!]){prefectureIndices(landUtilizationMethods:$landUtilizationMethods){prefectureId utilizationMethodKind published}}`;

const ACTIVE = "LandArea_tabButtonActive";

class SiteSharedLandAreaController extends Controller {
  static targets = ["linkList"];
  static values = { prefectures: Array, graphql: String, initialIndex: Number };

  declare readonly linkListTarget: HTMLUListElement;
  declare readonly prefecturesValue: Prefecture[];
  declare readonly graphqlValue: string;
  declare readonly initialIndexValue: number;

  private publishedSet = new Set<string>();
  private activeIndex = 0;

  connect(): void {
    // 活用方法ページは initialIndex (アパート等のタブ) から開始する。
    this.activeIndex = Number(this.initialIndexValue) || 0;
    this.loadPublished().then(() => {
      // 初期タブが tab0 以外なら、公開判定取得後にリンクを再描画してフィルタを反映する。
      if (this.activeIndex !== 0) this.renderLinks();
    });
  }

  private async loadPublished(): Promise<void> {
    try {
      const res = await fetch(this.graphqlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: QUERY,
          variables: { landUtilizationMethods: ["CONDOMINIUM", "HOUSE"] },
        }),
      });
      const json = await res.json();
      const indices = json?.data?.prefectureIndices || [];
      indices.forEach((idx: any) => {
        if (idx.published) {
          this.publishedSet.add(`${idx.prefectureId}_${idx.utilizationMethodKind}`);
        }
      });
    } catch (_e) {
      // 取得失敗時は土地活用タブ (全件リンク) のみ有効
    }
  }

  selectTab(event: { params: { index: number } }): void {
    this.activeIndex = Number(event.params.index);
    this.element
      .querySelectorAll<HTMLButtonElement>(".LandArea_tabButton")
      .forEach((btn, i) => btn.classList.toggle(ACTIVE, i === this.activeIndex));
    this.renderLinks();
  }

  private isPublished(prefId: number, kind: string | null): boolean {
    if (!kind) return true;
    return this.publishedSet.has(`${prefId}_${kind}`);
  }

  private renderLinks(): void {
    const tab = TABS[this.activeIndex];
    const html = REGIONS.map((region) => {
      const prefs = this.prefecturesValue.slice(region.start, region.end);
      const items = prefs
        .map((p) => {
          if (this.isPublished(p.id, tab.kind)) {
            return `<li><a href="/${p.url}/${tab.pathSuffix}">${p.name}</a></li>`;
          }
          return `<li><span class="LandArea_disabledLink">${p.name}</span></li>`;
        })
        .join("");
      return `<li><span class="LandArea_regionName">${region.name}</span><ul class="LandArea_separatedPrefecture">${items}</ul></li>`;
    }).join("");
    this.linkListTarget.innerHTML = html;
  }
}

application.register("site--shared--land-area", SiteSharedLandAreaController);
